import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ProductCategory } from "../../types";
import { CategoryMenuConfig } from "../../themes/themeTypes";
import { cn } from "../../utils/cn";
import { useLocalizedTo } from "../../hooks/useLocaleNavigation";

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return prefersReduced;
}

interface CategoryMenuMegaProps {
  categories: ProductCategory[];
  config: CategoryMenuConfig;
  className?: string;
  navLinkClassName?: string;
}

/**
 * Reads an image URL for a category out of metadata. Stores populate this via
 * `products.json` category entries (`metadata.image_url`). Falls back to null
 * so the renderer can decide how to handle missing images.
 */
function categoryImageUrl(category: ProductCategory): string | null {
  const meta = (category.metadata || {}) as Record<string, unknown>;
  const url = meta.image_url || meta.imageUrl || meta.image;
  return typeof url === "string" && url.length > 0 ? url : null;
}

const CategoryMenuMega: React.FC<CategoryMenuMegaProps> = ({
  categories,
  config,
  className,
  navLinkClassName,
}) => {
  const localizedTo = useLocalizedTo();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearCloseTimer();
    setIsOpen(true);
  }, [clearCloseTimer]);

  const handleMouseLeave = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 120);
  }, [clearCloseTimer]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  const prefersReducedMotion = usePrefersReducedMotion();

  const panelTransitionStyle: React.CSSProperties = useMemo(() => ({
    transition: prefersReducedMotion ? "none" : "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), visibility 200ms",
  }), [prefersReducedMotion]);

  if (!categories.length) return null;

  const triggerLabel = config.label || "Products";
  const imagesMode = config.showImages === true;

  type CategoryGroup = { parent: ProductCategory; children: ProductCategory[] };
  const imageGroups: CategoryGroup[] = imagesMode
    ? categories.map((parent) => {
        const children = (parent.category_children || [])
          .filter((c) => c.is_active && !c.is_internal)
          .sort((a, b) => a.rank - b.rank);
        return { parent, children };
      })
    : [];

  const panelOpenClass = isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none";

  return (
    <div
      ref={menuRef}
      className={cn("relative", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="category-menu-mega"
    >
      <button
        className={cn(
          "text-sm text-text-primary bg-transparent border-none cursor-pointer transition-all hover:text-primary whitespace-nowrap flex items-center gap-1 p-0 border-b-2 border-transparent hover:border-primary pb-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
          navLinkClassName,
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => setIsOpen((prev) => !prev)}
        data-testid="mega-menu-trigger"
      >
        {triggerLabel}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className={cn("transition-transform", isOpen && "rotate-180")}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {imagesMode && imageGroups.length > 0 && (
        <>
          <div
            className={cn("hidden md:block fixed left-0 right-0 h-3", panelOpenClass)}
            style={{ top: "var(--header-height, 80px)", ...panelTransitionStyle }}
          />
          <div
            className={cn(
              "hidden md:block fixed left-0 right-0 border-t border-border shadow-lg z-40",
              panelOpenClass,
            )}
            style={{
              top: "var(--header-height, 80px)",
              maxHeight: "calc(100vh - var(--header-height, 80px))",
              overflow: "auto",
              backgroundColor: "var(--color-bg)",
              ...panelTransitionStyle,
            }}
            data-testid="mega-menu-panel"
            data-variant="images"
          >
            <div
              className="container mx-auto px-lg py-lg"
              style={{ maxWidth: "var(--container-max-width)" }}
            >
              <div className="flex flex-wrap gap-xl">
                {imageGroups.map(({ parent, children }) => {
                  const cards = children.length > 0 ? children : [parent];
                  return (
                    <div
                      key={parent.id}
                      className="flex-1 min-w-0"
                      data-testid="mega-menu-group"
                    >
                      <a
                        href={localizedTo(`/categories/${parent.handle}`)}
                        className="block mb-md text-xs tracking-[0.15em] uppercase text-text-primary no-underline hover:text-primary transition-colors"
                        data-testid="mega-menu-group-heading"
                      >
                        {parent.name}
                      </a>
                      <div
                        className="grid gap-md"
                        style={{
                          gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))`,
                        }}
                      >
                        {cards.map((card) => {
                          const imgUrl = categoryImageUrl(card);
                          return (
                            <a
                              key={card.id}
                              href={localizedTo(`/categories/${card.handle}`)}
                              className="group block no-underline text-text-primary"
                              data-testid="mega-menu-image-card"
                            >
                              <div
                                className="relative aspect-square overflow-hidden bg-bg-secondary mb-sm"
                                style={{ maxHeight: "180px" }}
                              >
                                {imgUrl ? (
                                  <img
                                    src={imgUrl}
                                    alt={card.name}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                    style={{ transform: "scale(1.02)" }}
                                  />
                                ) : (
                                  <div
                                    className="w-full h-full flex items-center justify-center text-text-light"
                                    aria-hidden="true"
                                  >
                                    <svg
                                      width="40"
                                      height="40"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                    >
                                      <rect x="3" y="3" width="18" height="18" />
                                      <circle cx="8.5" cy="8.5" r="1.5" />
                                      <polyline points="21 15 16 10 5 21" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <p
                                className="text-xs text-center transition-colors group-hover:text-primary"
                                style={{ fontFamily: "var(--font-family-primary)" }}
                              >
                                {card.name}
                              </p>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {!imagesMode && (
        <>
          <div
            className={cn("absolute top-full left-0 w-full h-3", panelOpenClass)}
            style={panelTransitionStyle}
          />
          <div
            className={cn(
              "absolute top-full left-1/2 -translate-x-1/2 pt-2 bg-bg border border-border shadow-lg z-50 py-6 px-8",
              panelOpenClass,
            )}
            style={{
              width: "min(900px, 90vw)",
              marginTop: "0.25rem",
              ...panelTransitionStyle,
            }}
            data-testid="mega-menu-panel"
            data-variant="text"
          >
            <div
              className="grid gap-8"
              style={{ gridTemplateColumns: `repeat(${Math.min(categories.length, 4)}, 1fr)` }}
            >
              {categories.map((category) => {
                const children = (category.category_children || [])
                  .filter((c) => c.is_active && !c.is_internal)
                  .sort((a, b) => a.rank - b.rank);

                return (
                  <div key={category.id}>
                    <a
                      href={localizedTo(`/categories/${category.handle}`)}
                      className="text-sm font-semibold text-text-primary no-underline hover:text-primary transition-colors block mb-3"
                      data-testid="mega-menu-category-link"
                    >
                      {category.name}
                    </a>
                    {config.showDescriptions && category.description && (
                      <p className="text-xs text-text-light mb-3 line-clamp-2">{category.description}</p>
                    )}
                    {children.length > 0 && (
                      <ul className="list-none p-0 m-0 space-y-2">
                        {children.map((child) => (
                          <li key={child.id}>
                            <a
                              href={localizedTo(`/categories/${child.handle}`)}
                              className="text-sm text-text-secondary no-underline hover:text-primary transition-colors"
                              data-testid="mega-menu-child-link"
                            >
                              {child.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border-t border-border mt-6 pt-4">
              <a
                href={localizedTo('/products')}
                className="text-sm text-primary no-underline hover:underline font-medium"
              >
                View All Products
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryMenuMega;
